import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client';

const cardStyle: React.CSSProperties = {
  background: 'var(--color-bg-card)',
  borderRadius: 'var(--radius-md)',
  boxShadow: 'var(--shadow-sm)',
  border: '1px solid var(--color-border)',
  padding: '1.5rem',
  marginBottom: '1.5rem',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: 'var(--color-text-secondary)',
  marginBottom: '0.25rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: '0.875rem',
  background: 'var(--color-bg)',
  color: 'var(--color-text)',
  boxSizing: 'border-box',
  fontFamily: 'monospace',
};

export function AdminAiSettingsPage() {
  const qc = useQueryClient();
  const [apiKey, setApiKey] = useState('');
  const [saved, setSaved] = useState(false);

  const { data, isLoading } = useQuery<{ configured: boolean }>({
    queryKey: ['settings', 'ai-config'],
    queryFn: () => api.get<{ configured: boolean }>('/settings/ai-config').then(r => r.data),
  });

  const saveMutation = useMutation({
    mutationFn: (key: string) => api.post('/settings/ai-config', { apiKey: key }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'ai-config'] });
      setApiKey('');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
  });

  const removeMutation = useMutation({
    mutationFn: () => api.post('/settings/ai-config', { apiKey: '' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'ai-config'] });
    },
  });

  const configured = data?.configured ?? false;

  return (
    <div style={{ maxWidth: 580 }}>
      <div style={cardStyle}>
        <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.125rem', fontWeight: 700 }}>
          KI-Einstellungen
        </h2>
        <p style={{ margin: '0 0 1.5rem', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
          Konfigurieren Sie den Anthropic API-Key für den KI-Hilfe-Chat (❓ im Topbar).
        </p>

        {/* Status */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          marginBottom: '1.5rem',
          padding: '0.75rem 1rem',
          background: configured ? 'rgba(22,163,74,0.08)' : 'rgba(220,38,38,0.08)',
          borderRadius: 'var(--radius-sm)',
          border: `1px solid ${configured ? 'rgba(22,163,74,0.2)' : 'rgba(220,38,38,0.2)'}`,
        }}>
          <span style={{ fontSize: '0.875rem' }}>
            {configured ? '🟢' : '🔴'}
          </span>
          <span style={{
            fontSize: '0.875rem', fontWeight: 600,
            color: configured ? 'var(--color-success, #16a34a)' : 'var(--color-danger, #dc2626)',
          }}>
            {isLoading ? 'Prüfe...' : configured ? 'API-Key konfiguriert' : 'Kein API-Key hinterlegt'}
          </span>
          {configured && (
            <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginLeft: 'auto' }}>
              KI-Chat aktiv
            </span>
          )}
        </div>

        {/* Neuen Key setzen */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={labelStyle}>
            {configured ? 'Key ersetzen' : 'API-Key hinterlegen'}
          </label>
          <input
            style={inputStyle}
            type="password"
            value={apiKey}
            onChange={e => setApiKey(e.target.value)}
            placeholder="sk-ant-api03-..."
            autoComplete="off"
            spellCheck={false}
          />
          <p style={{ margin: '0.375rem 0 0', fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
            Den API-Key erhalten Sie unter{' '}
            <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer"
              style={{ color: 'var(--color-primary)' }}>
              console.anthropic.com
            </a>
            . Der Key wird verschlüsselt gespeichert und niemals zurückgegeben.
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <button
            style={{
              padding: '0.5rem 1.25rem',
              background: 'var(--color-primary)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: apiKey.trim() && !saveMutation.isPending ? 'pointer' : 'not-allowed',
              opacity: apiKey.trim() && !saveMutation.isPending ? 1 : 0.5,
            }}
            onClick={() => { if (apiKey.trim()) saveMutation.mutate(apiKey); }}
            disabled={!apiKey.trim() || saveMutation.isPending}
          >
            {saveMutation.isPending ? 'Speichern…' : 'Speichern'}
          </button>

          {configured && (
            <button
              style={{
                padding: '0.5rem 1rem',
                background: 'none',
                color: 'var(--color-danger, #dc2626)',
                border: '1px solid var(--color-danger, #dc2626)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: removeMutation.isPending ? 'not-allowed' : 'pointer',
                opacity: removeMutation.isPending ? 0.5 : 1,
              }}
              onClick={() => {
                if (confirm('API-Key wirklich entfernen? Der KI-Chat wird deaktiviert.')) {
                  removeMutation.mutate();
                }
              }}
              disabled={removeMutation.isPending}
            >
              {removeMutation.isPending ? 'Entfernen…' : 'Key entfernen'}
            </button>
          )}

          {saved && (
            <span style={{ color: 'var(--color-success, #16a34a)', fontSize: '0.875rem', fontWeight: 500 }}>
              Gespeichert ✓
            </span>
          )}
          {(saveMutation.isError || removeMutation.isError) && (
            <span style={{ color: 'var(--color-danger, #dc2626)', fontSize: '0.875rem' }}>
              Fehler beim Speichern
            </span>
          )}
        </div>
      </div>

      {/* Info-Box */}
      <div style={{
        ...cardStyle,
        background: 'var(--color-bg)',
        marginBottom: 0,
      }}>
        <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-text-secondary)' }}>
          ℹ️ Hinweise
        </h3>
        <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.8125rem', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
          <li>Der KI-Hilfe-Chat nutzt <strong>Claude Haiku</strong> (kostengünstiges Modell, ~0,001 $ pro Anfrage).</li>
          <li>Empfehlung: Usage-Limits im Anthropic-Dashboard setzen.</li>
          <li>Eine in der Server-<code>.env</code> gesetzte Variable <code>ANTHROPIC_API_KEY</code> hat Vorrang vor dem hier hinterlegten Key.</li>
          <li>Änderungen werden innerhalb von 60 Sekunden wirksam.</li>
        </ul>
      </div>
    </div>
  );
}
